import React, { useState } from "react";
import { addParticipant, getMySignups } from "../api";
import {
  MySignupResponse,
  ParticipantRequest,
  EventSignupResponse,
} from "../types";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useEffect, useCallback } from "react";
import { getEventSignup } from "../api";
import RichText from "./RichText";

const JWT_KEY = "magic-link-jwt";
const AUTH_CHANGED_EVENT = "auth-changed";

function getEmailFromJwt(jwt: string): string | null {
  try {
    const payload = JSON.parse(atob(jwt.split(".")[1]));
    return payload.email ?? null;
  } catch {
    return null;
  }
}

const useEventId = () => {
  const [searchParams] = useSearchParams();
  return searchParams.get("eventId");
};

const useIsTest = () => {
  const [searchParams] = useSearchParams();
  return searchParams.get("test") === "true";
};

const EventSignup: React.FC = () => {
  const eventId = useEventId();
  const isTest = useIsTest();
  const navigate = useNavigate();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: boolean;
  }>({});
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() =>
    Boolean(localStorage.getItem(JWT_KEY)),
  );
  const [mySignups, setMySignups] = useState<MySignupResponse[]>([]);
  const storageKey = `signed-up-participants-${eventId}`;
  const existingParticipants = JSON.parse(
    localStorage.getItem(storageKey) || "[]",
  );

  const [eventData, setEventData] = useState<EventSignupResponse | null>(null);

  useEffect(() => {
    if (!eventId) navigate("/home");
  }, [eventId, navigate]);

  useEffect(() => {
    const updateIsLoggedIn = () =>
      setIsLoggedIn(Boolean(localStorage.getItem(JWT_KEY)));
    const handleStorage = (e: StorageEvent) => {
      if (e.key === JWT_KEY) setIsLoggedIn(Boolean(e.newValue));
    };
    window.addEventListener("storage", handleStorage);
    window.addEventListener(AUTH_CHANGED_EVENT, updateIsLoggedIn);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(AUTH_CHANGED_EVENT, updateIsLoggedIn);
    };
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    const email = getEmailFromJwt(localStorage.getItem(JWT_KEY) ?? "");
    if (email) {
      setParticipant((prev) => ({ ...prev, contactEmail: email }));
    }
  }, [isLoggedIn]);

  const fetchEventSignup = useCallback(async () => {
    if (!eventId) return;
    try {
      const data = await getEventSignup(eventId);
      setEventData(data);
      if (data !== null) {
        // Initialize participant.alternatives based on eventData.alternatives
        setParticipant((prev) => ({
          ...prev,
          alternatives:
            data.alternatives?.map((alt) => ({
              id: alt.id,
              alternativeName: alt.alternativeName,
              alternativeText: alt.alternativeText,
              alternatives: [],
              multipleAnswers: alt.multipleAnswers,
              showInInfo: alt.showInInfo,
              isRequired: alt.isRequired,
            })) || [],
          extraFields:
            data.extraFields?.map((field) => ({
              id: field.id,
              name: field.name,
              value: "",
              isRequired: field.isRequired,
            })) || [],
          extraInfos:
            data.extraInfos?.map((info) => ({
              id: info.id,
              label: info.label,
              text: info.text,
            })) || [],
        }));
      }
    } catch (error) {
      console.error("Error fetching event signup:", error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchEventSignup();
  }, [fetchEventSignup]);

  useEffect(() => {
    if (!isLoggedIn) return;
    getMySignups(isTest)
      .then((signups) =>
        setMySignups(signups.filter((s) => String(s.event.id) === eventId)),
      )
      .catch(() => {});
  }, [isLoggedIn, eventId, isTest]);

  const loggedInEmail = isLoggedIn
    ? getEmailFromJwt(localStorage.getItem(JWT_KEY) ?? "")
    : null;

  const [participant, setParticipant] = useState<ParticipantRequest>({
    firstName: "",
    lastName: "",
    contactEmail: loggedInEmail ?? "",
    birthYear: null,
    haveAgreedConditions: false,
    haveAgreedStorage: false,
    alternatives:
      eventData?.alternatives?.map((alt) => ({
        id: alt.id,
        alternativeName: alt.alternativeName,
        alternativeText: alt.alternativeText,
        alternatives: [],
        multipleAnswers: alt.multipleAnswers,
        isRequired: alt.isRequired,
        showInInfo: alt.showInInfo,
      })) || [],
    extraFields:
      eventData?.extraFields?.map((field) => ({
        id: field.id,
        name: field.name,
        value: "",
        isRequired: field.isRequired,
      })) || [],
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setParticipant((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required alternatives
    const errors: { [key: string]: boolean } = {};
    eventData?.alternatives?.forEach((alt, index) => {
      if (
        alt.isRequired &&
        participant.alternatives[index].alternatives.length === 0
      ) {
        errors[`alternative-${index}`] = true;
      }
    });

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return; // Prevent form submission if there are validation errors
    }

    try {
      const res = await addParticipant(eventId ?? "", participant);
      if (res === true) {
        alert("Anmälan är inskickad.");
        setFormSubmitted(true);

        if (isLoggedIn) {
          getMySignups(isTest)
            .then((signups) =>
              setMySignups(
                signups.filter((s) => String(s.event.id) === eventId),
              ),
            )
            .catch(() => {});
        } else {
          const updatedParticipants = [...existingParticipants, participant];
          localStorage.setItem(storageKey, JSON.stringify(updatedParticipants));
        }
      } else {
        alert("Kontrollera att eventet ej har passerat sista anmälan.");
      }
    } catch (error) {
      console.error("Error adding participant:", error);
      alert("Failed to add participant.");
    }
  };

  return isError || isLoading ? (
    isLoading ? (
      <div>Laddar...</div>
    ) : isError ? (
      <div className="container">
        <h1>Fel vid visning av event</h1>
        <p>
          Vänligen kontrollera att du fått med rätt länk. Om den stämmer, testa
          gärna länken i en annan browser, ex chrome.
        </p>
      </div>
    ) : (
      <></>
    )
  ) : (
    <div className="container">
      <h1>{eventData?.eventName ?? "Sista anmälan har passerat"}</h1>
      {formSubmitted && (
        <>
          <h2>Tack för din anmälan</h2>
          <p style={{ textAlign: "center" }}>
            Din anmälan är inskickad, använd knappen längst ner för att anmäla
            fler personer, ex ett syskon. Du kommer bli kontaktad för mer
            information när sista anmälningsdatum har passerat.
          </p>
          <p style={{ textAlign: "center" }}>
            {eventData?.thanksForSigningUpText}
          </p>
        </>
      )}
      {/* Display signed-up participants */}
      {isLoggedIn && mySignups.length > 0 && (
        <>
          <div>
            <h2>Deltagare du anmält:</h2>
            <table className="table-container">
              <thead>
                <tr>
                  <th>Förnamn</th>
                  <th>Efternamn</th>
                  <th>Födelseår</th>
                </tr>
              </thead>
              <tbody>
                {mySignups.flatMap((signup) =>
                  signup.participants.map((p) => (
                    <tr key={p.id}>
                      <td>{p.firstName}</td>
                      <td>{p.lastName}</td>
                      <td>{p.birthYear}</td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>
          <h2>Information om {eventData?.eventName}:</h2>
        </>
      )}
      {!isLoggedIn && existingParticipants.length > 0 && (
        <>
          <div>
            <h2>Deltagare du anmält:</h2>
            <p>
              Observera att detta är endast deltagare du anmält från denna enhet
              och webbläsare.
            </p>
            <table className="table-container">
              <thead>
                <tr>
                  <th>Förnamn</th>
                  <th>Efternamn</th>
                  <th>E-post</th>
                  <th>Födelseår</th>
                </tr>
              </thead>
              <tbody>
                {existingParticipants.map(
                  (participant: ParticipantRequest, index: number) => (
                    <tr key={index}>
                      <td>{participant.firstName}</td>
                      <td>{participant.lastName}</td>
                      <td>{participant.contactEmail}</td>
                      <td>{participant.birthYear}</td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
          <h2>Information om {eventData?.eventName}:</h2>
        </>
      )}

      {!eventData && (
        <p>
          Sista anmälan har passerat och det går inte längre att anmäla sig.
        </p>
      )}
      {eventData && (
        <div>
          <table className="table-container">
            <tbody>
              <tr>
                <td colSpan={2}>
                  <RichText className="rich-text">
                    {eventData.description}
                  </RichText>
                </td>
              </tr>
              {eventData.targetGroup && (
                <tr>
                  <td>Målgrupp:</td>
                  <td>{eventData.targetGroup}</td>
                </tr>
              )}
              <tr>
                <td>Datum:</td>
                <td>
                  {eventData.displayTimeStartEndDate
                    ? `${new Date(eventData.startDate).toLocaleTimeString([], {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })} till ${new Date(eventData.endDate).toLocaleTimeString(
                        [],
                        {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        },
                      )}`
                    : `${new Date(eventData.startDate).toLocaleDateString([], {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })} till ${new Date(eventData.endDate).toLocaleDateString(
                        [],
                        {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        },
                      )}`}
                </td>
              </tr>
              {eventData.location && (
                <tr>
                  <td>Plats:</td>
                  <td>{eventData.location}</td>
                </tr>
              )}
              {eventData.costs && (
                <tr>
                  <td>Kostnad:</td>
                  <td>{eventData.costs}</td>
                </tr>
              )}
              {eventData.conditions && (
                <tr>
                  <td>Villkor:</td>
                  <td>{eventData.conditions}</td>
                </tr>
              )}
              {eventData.extraInfos && eventData.extraInfos?.length > 0 && (
                <>
                  {eventData.extraInfos.map((info) => (
                    <tr key={info.id}>
                      <td>{info.label}: </td>
                      <td>{info.text}</td>
                    </tr>
                  ))}
                </>
              )}
              {eventData.alternatives &&
                eventData.alternatives?.length > 0 &&
                eventData.alternatives.some((x) => x.showInInfo) && (
                  <tr>
                    <td>Övrigt:</td>
                    <td>
                      <ul>
                        {eventData.alternatives?.map(
                          (alternative, index) =>
                            alternative.showInInfo && (
                              <li key={index}>
                                <strong>{alternative.alternativeName}</strong>:{" "}
                                {alternative.alternativeText}
                                <ul>
                                  {alternative.alternatives?.map(
                                    (option, idx) => (
                                      <li key={idx}>{option}</li>
                                    ),
                                  )}
                                </ul>
                              </li>
                            ),
                        )}
                      </ul>
                    </td>
                  </tr>
                )}
              <tr>
                <td>Anmäl senast:</td>
                <td>
                  {new Date(eventData.lastSignup).toISOString().split("T")[0]}
                </td>
              </tr>
            </tbody>
          </table>

          {!formSubmitted && (
            <h2>
              {eventData.seniorsOnly ? "Anmäl dig nedan:" : "Anmäl er nedan:"}
            </h2>
          )}
          {!formSubmitted ? (
            <form onSubmit={handleSubmit}>
              <table className="table-container">
                <tbody>
                  <tr>
                    <td>
                      <label htmlFor="firstName">
                        {eventData.seniorsOnly
                          ? "Förnamn:"
                          : "Deltagarens förnamn:"}
                      </label>
                    </td>
                    <td>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={participant.firstName}
                        onChange={handleChange}
                        required
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label htmlFor="lastName">
                        {eventData.seniorsOnly
                          ? "Efternamn:"
                          : "Deltagarens efternamn:"}
                      </label>
                    </td>
                    <td>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={participant.lastName}
                        onChange={handleChange}
                        required
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label htmlFor="contactEmail">
                        {eventData.seniorsOnly
                          ? "E-post till deltagare:"
                          : "E-post till kontaktperson:"}
                      </label>
                    </td>
                    <td>
                      <input
                        type="email"
                        id="contactEmail"
                        name="contactEmail"
                        value={participant.contactEmail}
                        onChange={handleChange}
                        required
                        disabled={isLoggedIn}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label htmlFor="birthYear">Födelseår:</label>
                    </td>
                    <td>
                      <input
                        type="text"
                        id="birthYear"
                        name="birthYear"
                        value={participant.birthYear ?? 0}
                        onChange={handleChange}
                        required
                        pattern="[12]\d{3}" // Enforces exactly 4 digits
                        title="Födelseåret måste vara 4 siffror" // Tooltip for invalid input
                      />
                    </td>
                  </tr>
                  {eventData.extraFields &&
                    eventData.extraFields.map((field, index) => (
                      <tr key={index}>
                        <td>
                          <label htmlFor={`extraField-${index}`}>
                            {field.name}:
                          </label>
                        </td>
                        <td>
                          <input
                            type="string"
                            id={`extraField-${index}`}
                            name={`extraField-${index}`}
                            value={participant.extraFields?.[index].value || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setParticipant((prev) => {
                                const updatedExtraFields = [
                                  ...(prev.extraFields || []),
                                ];
                                updatedExtraFields[index].value = value;
                                return {
                                  ...prev,
                                  extraFields: updatedExtraFields,
                                };
                              });
                            }}
                            required={field.isRequired}
                            minLength={
                              field.isRequired ? field.requiredLength : 1
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  {eventData.alternatives?.map((alternative, index) => (
                    <tr key={index}>
                      <td>
                        <label>{alternative.alternativeName}:</label>
                      </td>
                      <td>
                        {alternative.multipleAnswers
                          ? alternative.alternatives?.map((option, idx) => (
                              <div key={idx}>
                                <label>
                                  <input
                                    type="checkbox"
                                    value={option}
                                    checked={participant.alternatives[
                                      index
                                    ].alternatives?.includes(option)}
                                    onChange={(e) => {
                                      setParticipant((prev) => {
                                        const updatedAnswers =
                                          prev.alternatives.map((answer) =>
                                            answer.id ===
                                            participant.alternatives[index].id
                                              ? {
                                                  ...answer,
                                                  alternatives: e.target.checked
                                                    ? [
                                                        ...answer.alternatives,
                                                        option,
                                                      ]
                                                    : answer.alternatives?.filter(
                                                        (ans) => ans !== option,
                                                      ),
                                                }
                                              : answer,
                                          );
                                        return {
                                          ...prev,
                                          alternatives: updatedAnswers,
                                        };
                                      });
                                    }}
                                  />
                                  {option}
                                </label>
                              </div>
                            ))
                          : alternative.alternatives?.map((option, idx) => (
                              <div key={idx}>
                                <label>
                                  <input
                                    type="radio"
                                    name={`alternative-${index}`}
                                    value={option}
                                    checked={participant.alternatives[
                                      index
                                    ].alternatives?.includes(option)}
                                    onChange={() => {
                                      setParticipant((prev) => {
                                        const updatedAnswers =
                                          prev.alternatives.map((answer) =>
                                            answer.id ===
                                            participant.alternatives[index].id
                                              ? {
                                                  ...answer,
                                                  alternatives: [option],
                                                }
                                              : answer,
                                          );
                                        return {
                                          ...prev,
                                          alternatives: updatedAnswers,
                                        };
                                      });
                                    }}
                                  />
                                  {option}
                                </label>
                              </div>
                            ))}
                        {/* Display validation error message */}
                        {validationErrors[`alternative-${index}`] && (
                          <span style={{ color: "red", marginLeft: "10px" }}>
                            Obligatoriskt fält
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td>
                      <label htmlFor="haveAgreedConditions">
                        {eventData.seniorsOnly
                          ? "Jag har läst och förstått villkoren för evenemanget:"
                          : "Vi har läst och förstått villkoren för evenemanget:"}
                      </label>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        id="haveAgreedConditions"
                        name="haveAgreedConditions"
                        checked={participant.haveAgreedConditions}
                        onChange={(e) =>
                          setParticipant((prev) => ({
                            ...prev,
                            haveAgreedConditions: e.target.checked,
                          }))
                        }
                        required
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label htmlFor="haveAgreedStorage">
                        {eventData.seniorsOnly
                          ? "Jag godkänner att angivna uppgifter sparas:"
                          : "Vi godkänner att angivna uppgifter sparas:"}
                      </label>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        id="haveAgreedStorage"
                        name="haveAgreedStorage"
                        checked={participant.haveAgreedStorage}
                        onChange={(e) =>
                          setParticipant((prev) => ({
                            ...prev,
                            haveAgreedStorage: e.target.checked,
                          }))
                        }
                        required
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <button
                className="add-item-button"
                type="submit"
                disabled={
                  !participant.firstName ||
                  !participant.lastName ||
                  !participant.contactEmail ||
                  !participant.haveAgreedConditions ||
                  !participant.haveAgreedStorage
                }
              >
                Skicka in
              </button>
            </form>
          ) : (
            <div>
              <button
                className="add-item-button"
                onClick={() => {
                  setParticipant({
                    firstName: "",
                    lastName: "",
                    contactEmail: "",
                    birthYear: null,
                    haveAgreedConditions: false,
                    haveAgreedStorage: false,
                    alternatives:
                      eventData?.alternatives?.map((alt) => ({
                        id: alt.id,
                        alternativeName: alt.alternativeName,
                        alternativeText: alt.alternativeText,
                        alternatives: [],
                        multipleAnswers: alt.multipleAnswers,
                        isRequired: alt.isRequired,
                        showInInfo: alt.showInInfo,
                      })) || [],
                    extraFields:
                      eventData?.extraFields?.map((field) => ({
                        id: field.id,
                        name: field.name,
                        value: "",
                        isRequired: field.isRequired,
                      })) || [],
                  });
                  setFormSubmitted(false);
                }}
              >
                Gör ytterligare anmälan
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventSignup;
